// components/Map.jsx
import React, {useState, useEffect, useRef, useCallback} from 'react';
import PropTypes from 'prop-types';
import ReactMapGL, {Source, Layer} from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
// import '../stylesheets/widgets/mapbox-gl-draw.scss';
import { COLORS } from './theme';
import circle from "@turf/circle"
import { lineString } from "@turf/helpers"
import {Editor, DrawPolygonMode, EditingMode, RENDER_STATE} from 'react-map-gl-draw';
import isEqual from 'lodash/isEqual';
import isEqualWith from 'lodash/isEqualWith';
// installed react-map-gl-draw version 0.21.1 instead of latest 0.22.3 because it was impossible to edit features with later versions

const layerFromLevel = {
  0: "countries",
  1: "regions",
  2: "departments",
  3: "cities",
  4: "iris"
}

const zoomForLayer = {
  "countries": 3,
  "regions": 4,
  "departments": 7,
  "cities": 10,
  "iris": 12
}

/*
Map manipulates features with the following shape: {id, propertyId, source, layer, name}
Map communicates with the outside world (selected, hovered, onHover, onChange) with objects of the following shape: {level, country, name, value}
*/

function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

const featureEquality = (feature1, feature2) => {
  const filteredFeature1 = feature1 ? {propertyId: feature1.propertyId, layer: feature1.layer} : null
  const filteredFeature2 = feature2 ? {propertyId: feature2.propertyId, layer: feature2.layer} : null
  return isEqual(filteredFeature1, filteredFeature2)
}

const getEditHandleStyle = ({feature, state}) => {
  switch (state) {
    case RENDER_STATE.SELECTED:
    case RENDER_STATE.HOVERED:
    case RENDER_STATE.UNCOMMITTED:
      return {
        fill: COLORS.primary,
        fillOpacity: 1,
        stroke: 'rgb(255, 255, 255)',
        strokeWidth: 2,
        r: 7
      };

    default:
      return {
        fill: COLORS.primary,
        fillOpacity: 1,
        stroke: 'rgb(255, 255, 255)',
        strokeWidth: 2,
        r: 5
      };
  }
}

const getFeatureStyle = ({feature, index, state}) => {
  switch (state) {
    case RENDER_STATE.SELECTED:
    case RENDER_STATE.UNCOMMITTED:
    case RENDER_STATE.CLOSING:
      return {
        stroke: COLORS.primary,
        strokeWidth: 2,
        fill: COLORS.primary,
        fillOpacity: 0.5,
        strokeDasharray: '4,2'
      };
    case RENDER_STATE.HOVERED:
      return {
        stroke: COLORS.primary,
        strokeWidth: 2,
        fill: COLORS.primary,
        fillOpacity: 0.5,
        strokeDasharray: '4,2',
        cursor: 'pointer'
      };
    default:
      return {
        stroke: COLORS.primary,
        strokeWidth: 2,
        fill: COLORS.primary,
        fillOpacity: 0.3
      };
  }
}

const MODES = [
  { id: 'drawPolygon', handler: DrawPolygonMode },
  { id: 'editing', handler: EditingMode },
];

function Map({onChange, onHover, ...props}) {
  const mapRef = useRef(null);
  const [viewport, setViewport] = useState({
    width: '100%',
    height: '100%',
    latitude: 48.863730,
    longitude: 2.357882,
    zoom: props.zoomLevel || 3
  });
  const [map, setMap] = useState(null);
  const [hovered, setHovered] = useState({});
  const prevHovered = usePrevious(hovered);
  const [selected, setSelected] = useState([]);
  const [pointData, setPointData] = useState(props.pointData);
  const [disabled, setDisabled] = useState(props.disabled);
  const [interactiveLayer, setInteractiveLayer] = useState(props.interactiveLayer)

  // Drawing
  // const [geojson, setGeojson] = useState(props.geojson);
  const [draw, setDraw] = useState(props.draw)
  const [modeId, setModeId] = useState('editing');
  const [modeHandler, setModeHandler] = useState(null);
  const [features, setFeatures] = useState([])
  const [selectedFeatureIndex, setSelectedFeatureIndex] = useState(null);
  const editorRef = useRef(null);

  useEffect(() => {
    if (props.geojson) {
      if (props.geojson.type === 'Feature') {
        setFeatures([props.geojson])
      } else if (props.geojson.type === 'FeatureCollection') {
        setFeatures(props.geojson.features)
      }
    }
  }, [props.geojson])

  useEffect(() => {
    setDraw(props.draw)
  }, [props.draw])

  const handleLoad = () => { setMap(mapRef.current.getMap()) }

  const inputToFeature = (input) => {
    if (input) {
      return {
        layer: input.layer || layerFromLevel[input.level],
        propertyId: input.value,
        sources: input.sources,
        name: input.name,
        country: input.country
      }
    }
  }

  const featureToOutput = (feature) => {
    if (feature) {
      return {
        value: feature.propertyId,
        level: feature.level,
        country: feature.country ? feature.country.toLowerCase() : feature.country,
        name: feature.name,
        parents: feature.parents,
      }
    } else {
      return null
    }
  }

  const buildFeature = (feature, opts = {}) => {
    if (feature) {
      const id = feature.id
      const propertyId = feature.properties.id
      const source = opts.source || feature.layer.source
      const layer = opts.layer || (feature.layer ? feature.layer["source-layer"] : null)
      const name = feature.properties.name || opts.name //TEMPORARY
      const country = feature.properties.country_iso_a2 ? feature.properties.country_iso_a2.toLowerCase() : opts.country //TEMPORARY
      const parents = feature.properties.parents ? JSON.parse(feature.properties.parents) : null
      const level = feature.properties.level || 0
      const obj = {id, propertyId, source, layer, name, country, parents, level}
      return obj
    } else {
      return {
        ...opts,
        level: parseInt(Object.keys(layerFromLevel).find(key => layerFromLevel[key] === opts.layer))
      }
    }
  }

  useEffect(() => {
    setDisabled(props.disabled)
  }, [props.disabled])

  useEffect(() => {
    if (props.interactiveLayer) {
      const zoom = zoomForLayer[props.interactiveLayer]
      setViewport({...viewport, zoom})
      setInteractiveLayer(props.interactiveLayer)
    }
  }, [props.interactiveLayer])

  useEffect(() => {
    if (props.interactiveLayer) {
      const zoom = viewport.zoom
      let layer = "countries"
      for (const [key, value] of Object.entries(zoomForLayer)) {
        if (zoom >= value) {
          layer = key
        }
      }
      if (layer != interactiveLayer) {
        setInteractiveLayer(layer)
      }
    }
    getMissingSelectedFeatures()
  }, [viewport])

  useEffect(() => {
    const hoveredChanged = !isEqualWith(hovered, prevHovered, featureEquality)
    if (hoveredChanged) {
      prevHovered && setFeatureState(prevHovered, {hover: false})
      hovered && setFeatureState(hovered, {hover: true})
    }
  }, [hovered.propertyId, hovered.layer])

  useEffect(() => {
    setPointData(props.pointData)
  }, [props.pointData])

  useEffect(() => {
    if (map) {
      const feature = inputToFeature(props.hovered)
      const mapFeature = feature && getFeature(feature)
      setHovered( mapFeature || {})
    }
  }, [props.hovered, map])

  // When selected is changed from parent component
  useEffect(() => {
    if (props.selected && map) {
      const value = props.selected.map(x => inputToFeature(x))
      const oldSelected = selected.filter(x => !value.some(y => isEqualWith(x,y, featureEquality)))
      const newSelected = value.filter(x => !selected.some(y => isEqualWith(x,y, featureEquality))).map(element => getFeature(element))

      let newSelection = selected;
      oldSelected.map(oldItem => {
        newSelection = newSelection.filter(x => x.id !== oldItem.id)
        setFeatureState(oldItem, {selected: false})
      })
      newSelected.map(newItem => {
        newSelection = [...newSelection, newItem]
        setFeatureState(newItem, {selected: true})
      })
      setSelected(newSelection)
    }
  }, [props.selected, map])

  const getSource = (element) => {
    let source
    if (element.layer) {
      source = 'composite'
    } else {
      element.sources.forEach(s => {
        if (map.querySourceFeatures(s).length > 0 && !source) {
          source = s
        }
      })
    }
    return source
  }

  const getFeature = (element) => {
    const propertyId = element.propertyId
    const layer = element.layer
    const source = getSource(element)
    const feature = map.querySourceFeatures(source, {
      sourceLayer: layer,
      filter: ["==", ["get", "id"], propertyId]
    })[0]
    return buildFeature(feature, {source, layer, propertyId, name: element.name, country: element.country})
  }

  const getMissingSelectedFeatures = () => {
    const selectedCopy = [...selected]
    let shouldSetSelected = false
    selected.forEach((element, index) => {
      if (!element.id) {
        const feature = getFeature(element)
        if (feature.id) {
          shouldSetSelected = true
          selectedCopy[index] = feature
          setFeatureState(feature, {selected: true})
        }
      }
    })
    if (shouldSetSelected) {
      setSelected(selectedCopy)
    }
  }

  const setFeatureState = (feature, property) => {
    map && feature && feature.id && map.setFeatureState({id: feature.id, source: feature.source, sourceLayer: feature.layer}, property)
  }

  const handleHover = (event) => {
    const feature = event.features[0]
    const newHovered = buildFeature(feature)
    onHover && onHover(featureToOutput(newHovered))
    setHovered(newHovered || {})
  }

  const isAlreadySelectedFromParent = (feature) => {
    if (feature.parents) {
      return selected.some(x => Object.keys(feature.parents).some(y => x.level == y && x.propertyId.toLowerCase() == feature.parents[y].toLowerCase()))
    } else {
      return false
    }
  }

  const childrenSelected = (feature) => {
    return selected.filter(x => x.parents && Object.keys(x.parents).some(y => feature.level == y && feature.propertyId.toLowerCase() == x.parents[y].toLowerCase()))
  }

  const handleClick = (event) => {
    const feature = event.features[0]
    if (feature) {
      const newSelected = buildFeature(feature)
      if (newSelected) {
        let newSelection;
        if (selected.map(x => x.id).includes(newSelected.id)) {
          newSelection = selected.filter(x => x.id !== newSelected.id)
          setFeatureState(newSelected, {selected: false})
        } else {
          newSelection = selected
          if (!isAlreadySelectedFromParent(newSelected)) {
            newSelection = [...newSelection, newSelected]
            setFeatureState(newSelected, {selected: true})
          }
          let children = childrenSelected(newSelected)
          children.forEach(child => {
            const index = newSelection.findIndex(x => isEqualWith(x,child, featureEquality))
            newSelection.splice(index, 1)
            setFeatureState(child, {selected: false})
          })
        }
        setSelected(newSelection)
      }
    }
  }

  const handleMouseOut = () => {
    setHovered( {})
  }

  useEffect(() => {
    onChange && onChange(selected.map(x => featureToOutput(x)))
  }, [selected])

  const interactiveProps = () => {
    const interactiveHandlers = {
      onLoad: handleLoad,
      onHover: handleHover,
      onClick: handleClick,
      onMouseOut: handleMouseOut
    }
    if (interactiveLayer) {
      return {
        ...interactiveHandlers,
        interactiveLayerIds: disabled ? [] : [interactiveLayer]
      }
    } else if (pointData && pointData.properties.interactive) {
      return {
        ...interactiveHandlers,
        interactiveLayerIds: disabled ? [] : ['fill', 'point']
      }
    } else {
      return null
    }
  }

  const pointDataLayer = {
    id: 'point',
    type: 'circle',
    beforeId: 'poi-label',
    paint: {
      'circle-radius': ["case",
        ["boolean", ["feature-state", "selected"], false],
        6,
        ["boolean", ["feature-state", "hover"], false],
        6,
        4
      ],
      'circle-color': COLORS.primary
    }
  }

  const fillDataLayer = {
    id: 'fill',
    type: 'fill',
    beforeId: 'poi-label',
    paint: {
      'fill-color': COLORS.primary,
      'fill-opacity': ["case",
        ["boolean", ["feature-state", "hover"], false],
        1,
        ["boolean", ["feature-state", "selected"], false],
        1,
        0.5
      ]
    }
  }

  const lineDataLayer = {
    id: 'line',
    type: 'line',
    beforeId: 'poi-label',
    paint: {
      'line-color': COLORS.primary,
      "line-width": 2
    }
  }

  const circleData = () => ({
    type: 'FeatureCollection',
    features: pointData.features.map(feature => circle(feature.geometry.coordinates, feature.properties.radius/1000, {properties: feature.properties}))
  })

  const lineData = () => ({
    type: 'FeatureCollection',
    features: circleData().features.map(feature => lineString(...feature.geometry.coordinates))
  })

  // Drawing

  useEffect(() => {
    const mode = MODES.find((m) => m.id === modeId);
    setModeHandler(mode ? new mode.handler() : null)
  }, [modeId])

  const onSelect = useCallback(options => {
    setSelectedFeatureIndex(options && options.selectedFeatureIndex);
  }, []);

  const onDelete = useCallback(() => {
    if (selectedFeatureIndex !== null && selectedFeatureIndex >= 0) {
      let newFeatures = features.slice()
      newFeatures.splice(selectedFeatureIndex, 1)
      onChange(buildGeojson(newFeatures))
    }
  }, [selectedFeatureIndex]);

  const onUpdate = useCallback(({editType, data}) => {
    if (editType === 'addFeature') {
      setModeId('editing');
      onChange(buildGeojson(data))
    } else if (editType === 'movePosition') {
      onChange(buildGeojson(data))
    } else {
      setFeatures(data)
    }
  }, []);

  const buildGeojson = (data) => {
    if (data.length === 1) {
      return data[0]
    } else if (data.length > 1) {
      return {"type": "FeatureCollection", "features": data}
    } else if (data.length === 0) {
      return []
    }
  }

  const drawTools = (
    <div className="mapboxgl-ctrl-top-left">
      <div className="mapboxgl-ctrl-group mapboxgl-ctrl">
        <button
          type="button"
          className={`mapbox-gl-draw_ctrl-draw-btn mapbox-gl-draw_polygon ${modeId === 'drawPolygon' ? 'selected' : ''}`}
          title="Polygon tool (p)"
          onClick={() => {
            if (modeId === 'drawPolygon') {
              setModeId('editing')
            } else {
              setModeId('drawPolygon')
            }
          }}
        />
        <button
          type="button"
          className="mapbox-gl-draw_ctrl-draw-btn mapbox-gl-draw_trash"
          title="Delete"
          onClick={onDelete}
        />
      </div>
    </div>
  );

  return (
    <ReactMapGL
      {...viewport}
      {...interactiveProps()}
      ref={mapRef}
      mapboxApiAccessToken={process.env.MAPBOX_API_ACCESS_TOKEN}
      onViewportChange={nextViewport => setViewport(nextViewport)}
      mapStyle="mapbox://styles/singlespot/cm41gzo3300vq01s849xte181"
    >
      {pointData && <Source id="point" type="geojson" data={pointData} promoteId="id">
        <Layer {...pointDataLayer} />
      </Source>}
      {pointData && lineData() && <Source id="line" type="geojson" data={lineData()} generateId>
        <Layer {...lineDataLayer} />
      </Source>}
      {pointData && circleData() && <Source id="circle" type="geojson" data={circleData()} promoteId="id">
        <Layer {...fillDataLayer} />
      </Source>}
      {draw && <Editor
        ref={editorRef}
        clickRadius={12}
        mode={modeHandler}
        onSelect={onSelect}
        onUpdate={onUpdate}
        editHandleShape={'circle'}
        featureStyle={getFeatureStyle}
        editHandleStyle={getEditHandleStyle}
        features={features}
        style={{
          width: '100%', height: '100%',
          cursor: `${modeId === 'drawPolygon' ? 'crosshair' : 'inherit'}`
        }}
      />}
      {draw && !disabled && drawTools}
      {hovered.name && <div style={{position: "absolute", top: "10px", right: "10px", backgroundColor: "#FFFFFF88", padding: "5px 10px", borderRadius: "3px"}}>
        {hovered.name}
      </div>}
    </ReactMapGL>
  );
}

export default Map

Map.propTypes = {
  onChange: PropTypes.func,
  selected: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    level: (props) => {
      PropTypes.number
      if (props.level == null && !props.layer && !props.sources) {
        return new Error('Level, layer or sources must be provided for selected')
      }
    },
    layer: (props) => {
      PropTypes.string
      if (props.level == null && !props.layer && !props.sources) {
        return new Error('Level, layer or sources must be provided for selected')
      }
    },
    sources: (props) => {
      PropTypes.arrayOf(PropTypes.string)
      if (props.level == null && !props.layer && !props.sources) {
        return new Error('Level, layer or sources must be provided for selected')
      }
    },
    country: PropTypes.string,
    name: PropTypes.string
  })),
  hovered: PropTypes.shape({
    value: PropTypes.string,
    level: (props) => {
      PropTypes.number
      if (props.level == null && !props.layer && !props.sources) {
        return new Error('Level, layer or sources must be provided for hovered')
      }
    },
    layer: (props) => {
      PropTypes.string
      if (props.level == null && !props.layer && !props.sources) {
        return new Error('Level, layer or sources must be provided for hovered')
      }
    },
    sources: (props) => {
      PropTypes.arrayOf(PropTypes.string)
      if (props.level == null && !props.layer && !props.sources) {
        return new Error('Level, layer or sources must be provided for hovered')
      }
    },
    country: PropTypes.string,
    name: PropTypes.string
  }),
  interactiveLayer: PropTypes.string,
  interactiveLayers: PropTypes.arrayOf(PropTypes.string),
  layer: PropTypes.string,
  zoomLevel: PropTypes.number,
  pointData: PropTypes.shape({
    type: PropTypes.string.isRequired,
    properties: PropTypes.object,
    features: PropTypes.arrayOf(PropTypes.object).isRequired
  }),
  onHover: PropTypes.func
}

Map.defaultProps = {
  draw: true
}
